import { Link } from "react-router-dom";
import { assets } from "@core/assets";
import IconMenuOptions from "../DropDown/IconMenuOptions";
import Avatar from "@core/components/UI/Avatar";
import { getInitials } from "@core/utils/initials/initials";
import { Icon } from "@core/components/UI/Icon";
import { cn } from "@core/utils/cn";

type MenuOption = {
  id: string;
  name: string;
  label?: string;
};

type SideBarHeaderProps = {
  link?: string;
  initials?: string;
  title?: string;
  badgeTitle?: string;
  menuOptions?: MenuOption[];
  onWorkspaceSelect?: (workspaceName: string) => void;
  backHome?: boolean;
  is_switch?: boolean;
  collapsed?: boolean;
};

const SideBarHeader = ({
  link,
  title,
  badgeTitle,
  menuOptions,
  backHome = false,
  is_switch = false,
  collapsed = false,
  onWorkspaceSelect,
}: SideBarHeaderProps) => {
  const transformedMenuOptions =
    menuOptions?.map((workspace) => ({
      name: workspace.name,
      label: workspace.label,
      key: workspace.id.toString(),
      action: () => onWorkspaceSelect?.(workspace.name),
    })) || [];

  // Collapsed (icon-only): center the workspace switcher, or the avatar
  // when there is no switcher. Name, badge and back arrow are hidden.
  if (collapsed) {
    return (
      <div className="w-full h-[var(--app-header-h)] min-h-[var(--app-header-h)] shrink-0 px-2 flex items-center justify-center border-b border-neutral-200 bg-white overflow-hidden">
        {is_switch ? (
          <IconMenuOptions
            options={transformedMenuOptions}
            icon={assets["arrow-right-left"]}
            iconClassName="w-5 h-5"
            currentOption={menuOptions
              ?.find((w) => w.name === title)
              ?.id.toString()}
          />
        ) : (
          <Avatar shape="square" size="md" variantColor="gradiant">
            {title ? getInitials(title) : "SM"}
          </Avatar>
        )}
      </div>
    );
  }

  return (
    <div className="w-full h-[var(--app-header-h)] min-h-[var(--app-header-h)] shrink-0 px-3 flex items-center justify-between gap-2 border-b border-neutral-200 bg-white overflow-hidden">
      {/* Left group */}
      <div className="flex items-center gap-2 flex-1 min-w-0">
        {/* Back arrow — visible at all sizes when backHome */}
        {backHome && (
          <Link
            to={link ?? "/"}
            className="shrink-0 flex items-center justify-center w-7 h-7 rounded text-neutral-700 hover:text-neutral-950 hover:bg-surface-hover/50 transition-colors"
            aria-label="Return to home"
          >
            <Icon name="arrow-left" className="w-[18px] h-[18px]" />
          </Link>
        )}

        {/* Avatar — always visible */}
        <Avatar
          shape="square"
          size="md"
          variantColor="gradiant"
          className={cn(backHome && "hidden lg:flex")}
        >
          {title ? getInitials(title) : "SM"}
        </Avatar>

        {/* Name + badge — visible at all sizes */}
        <div className="flex flex-col min-w-0">
          <h5 className="text-sm font-medium capitalize truncate text-secondary-900 leading-tight">
            {title || "my workspace"}
          </h5>
          {badgeTitle && (
            <span className="mt-0.5 text-[8px] font-semibold px-1.5 py-px rounded-full bg-neutral-100 border border-neutral-200 text-neutral-500 self-start leading-tight">
              {badgeTitle}
            </span>
          )}
        </div>
      </div>

      {/* Right — workspace switcher, visible at all sizes */}
      {is_switch && (
        <div className="shrink-0">
          <IconMenuOptions
            options={transformedMenuOptions}
            icon={assets["arrow-right-left"]}
            iconClassName="w-5 h-5"
            currentOption={menuOptions
              ?.find((w) => w.name === title)
              ?.id.toString()}
          />
        </div>
      )}
    </div>
  );
};

export default SideBarHeader;
